import { getCookie, hasCookie, setCookie } from 'cookies-next';
import { assocPath, isEmpty, path, toPairs } from 'ramda';

import { reportClientError } from '@/lib/bugsnag';
import { pushToDataLayer } from '@/lib/gtm';
import {
  parseExperimentCookie,
  stringifyExperimentCookie,
} from '@/lib/utils/experiments';

export type AbTasty = {
  getTestsOnPage: (campaignId?: number) => AbTastyTestsOnPage;
};

export type AbTastyCampaignId = `${number}`;

export type AbTastyTestsOnPage = Record<
  AbTastyCampaignId,
  {
    additional_type: 'patch' | string;
    name: string;
    sub_type: 'ab' | 'mpt' | string;
    testDatas: { parentID: number };
    variationID: number;
    variationName: string;
  }
>;

export type AbTastyCampaign = {
  campaignId: AbTastyCampaignId;
  pageCampaignId: string;
  pageVariationId: string;
  variationId: string;
  variationName: string;
};

export const getAbTastyCampaigns = (
  abTastyTests: AbTastyTestsOnPage | null
): AbTastyCampaign[] =>
  isEmpty(abTastyTests ?? {})
    ? []
    : (toPairs(abTastyTests ?? {})
        .map(([campaignId, campaign]): AbTastyCampaign | null => {
          const {
            additional_type: additionalType,
            sub_type: subType,
            testDatas: { parentID: parentId },
            variationID: variationId,
            variationName,
          } = campaign;

          // Experiments of type patch are not considered experiments
          if (additionalType === 'patch') return null;

          // Under some mysterious conditions variationName is null
          if (!campaign || !variationName) {
            void reportClientError(new Error('Weird AB Tasty test error'), {
              isUnhandled: false,
              metadata: {
                'AB Tasty campaign': campaign,
              },
            });
            return null;
          }

          return {
            campaignId:
              subType === 'mpt'
                ? (String(parentId) as `${number}`)
                : campaignId,
            pageCampaignId: campaignId,
            pageVariationId: String(variationId),
            variationId:
              variationName === 'Original'
                ? '0'
                : (variationName.split(' ').at(-1) as string),
            variationName,
          };
        })
        .filter((campaign) => campaign !== null) as AbTastyCampaign[]);

export const handleAbTastyImpression = (
  pathname: string | null,
  campaigns: AbTastyCampaign[]
): void => {
  if (!pathname || isEmpty(campaigns)) return;

  // Send impressions to GTM for Piwik tracking
  campaigns.forEach((campaign) =>
    pushToDataLayer({
      event: 'gaEvent',
      eventAction: 'ab_tasty',
      eventCategory: 'experiment_impression',
      eventLabel: campaign.campaignId,
      experiment_id: campaign.campaignId,
      experiment_page_id: campaign.pageCampaignId,
      variant_id: `${campaign.campaignId}.${campaign.variationId}`,
      variant_name: campaign.variationName,
      variant_page_id: campaign.pageVariationId,
    })
  );

  // Update our cookie for sending to Salesforce later
  const structuredCookie = hasCookie('ih_ab_tasty')
    ? parseExperimentCookie(getCookie('ih_ab_tasty') as string)
    : {};

  const updatedStructure = campaigns
    .filter(({ campaignId }) => !path([pathname, campaignId], structuredCookie))
    .reduce(
      (acc, { campaignId, variationId }) =>
        assocPath([pathname, campaignId], variationId, acc),
      structuredCookie
    );

  const updatedCookie = stringifyExperimentCookie(updatedStructure);
  setCookie('ih_ab_tasty', updatedCookie);
};
